import type { FormEvent } from "react"
import { useMemo, useState } from "react"
import { format, set, startOfToday } from "date-fns"
import { IconClockSnoozeOutline18 as ClockSnooze } from "nucleo-ui-outline-18"
import { create } from "zustand"

import { Button, Input, Shortcut } from "@productlane/ui"
import { Calendar } from "@productlane/ui/src/components/internal/calendar"
import {
	Command,
	CommandDialog,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"

import { toast } from "@/components/toast"
import {
	formatSnoozeTime,
	getSnoozePresets,
	parseSnoozeQuery,
} from "./snooze-options"

interface SnoozeDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	onSelect: (value: Date) => void
}

export function SnoozeDialog({
	open,
	onOpenChange,
	onSelect,
}: SnoozeDialogProps) {
	const [view, setView] = useState<"options" | "custom">("options")
	const [wasOpen, setWasOpen] = useState(open)
	if (open !== wasOpen) {
		setWasOpen(open)
		if (open) setView("options")
	}

	return (
		<CommandDialog
			open={open}
			onOpenChange={onOpenChange}
			className="max-w-xl"
			onEscapeKeyDown={(e) => {
				if (view === "custom") {
					e.preventDefault()
					setView("options")
				}
			}}
		>
			{view === "custom" ? (
				<CustomSnoozeView onSelect={onSelect} />
			) : (
				<SnoozeOptionsView
					onSelect={onSelect}
					onCustom={() => setView("custom")}
				/>
			)}
		</CommandDialog>
	)
}

const useSnoozeDialogRequest = create<{
	onSelect: ((value: Date) => void) | null
}>()(() => ({ onSelect: null }))

/** Opens the H snooze picker outside a thread view, e.g. for a multi-select bulk snooze. */
export function pickSnoozeTime(onSelect: (value: Date) => void) {
	useSnoozeDialogRequest.setState({ onSelect })
}

export function SnoozeDialogHost() {
	const onSelect = useSnoozeDialogRequest((s) => s.onSelect)
	const close = () => useSnoozeDialogRequest.setState({ onSelect: null })

	return (
		<SnoozeDialog
			open={onSelect !== null}
			onOpenChange={(open) => {
				if (!open) close()
			}}
			onSelect={(value) => {
				close()
				onSelect?.(value)
			}}
		/>
	)
}

function SnoozeOptionsView({
	onSelect,
	onCustom,
}: {
	onSelect: (value: Date) => void
	onCustom: () => void
}) {
	const [query, setQuery] = useState("")

	const options = useMemo(() => {
		const now = new Date()
		const parsed = parseSnoozeQuery(query, now)
		return (query.trim() ? parsed : getSnoozePresets(now)).map((option) => ({
			...option,
			hint: formatSnoozeTime(option.value, now),
		}))
	}, [query])

	return (
		<Command shouldFilter={false} loop>
			<CommandInput
				Icon={ClockSnooze}
				autoFocus
				placeholder="Try: 1 minute, 1 hour, 1 day, 1 week"
				value={query}
				onValueChange={setQuery}
				className="px-2 py-5 text-base"
			/>
			<CommandList className="no-scrollbar">
				<CommandEmpty>No matching time</CommandEmpty>
				<CommandGroup>
					{options.map((option) => (
						<CommandItem
							key={`${option.label}-${option.value.getTime()}`}
							value={`${option.label}-${option.value.getTime()}`}
							size="lg"
							className="py-2.5 pl-3"
							hint={option.hint}
							onSelect={() => onSelect(option.value)}
						>
							{option.label}
						</CommandItem>
					))}
					{!query.trim() && (
						<CommandItem
							value="custom"
							size="lg"
							className="py-2.5 pl-3"
							onSelect={onCustom}
						>
							Custom
						</CommandItem>
					)}
				</CommandGroup>
			</CommandList>
			<KeyboardHints
				hints={[
					{ keys: ["↑", "↓"], label: "to navigate" },
					{ keys: ["⏎"], label: "to select" },
					{ keys: ["esc"], label: "to close" },
				]}
			/>
		</Command>
	)
}

function CustomSnoozeView({ onSelect }: { onSelect: (value: Date) => void }) {
	const [date, setDate] = useState<Date>(() => startOfToday())
	const [time, setTime] = useState("09:00")

	const submit = (e: FormEvent) => {
		e.preventDefault()
		const [hours = 9, minutes = 0] = time.split(":").map(Number)
		const value = set(date, { hours, minutes, seconds: 0, milliseconds: 0 })
		if (value.getTime() <= Date.now()) {
			toast.info({
				icon: ClockSnooze,
				title: `Pick a time after ${format(new Date(), "h:mm a")}`,
			})
			return
		}
		onSelect(value)
	}

	return (
		<form onSubmit={submit} className="flex flex-col">
			<div className="flex items-center gap-3 border-b border-gray-200 px-5 py-5 text-base text-primary dark:border-gray-700/80">
				<ClockSnooze className="h-4 w-4 flex-none text-secondary" />
				<span>Snooze until {format(date, "EEE, MMM d")}</span>
			</div>
			<div className="flex justify-center p-3">
				<Calendar
					mode="single"
					selected={date}
					onSelect={setDate}
					disabled={{ before: startOfToday() }}
					required
					autoFocus
				/>
			</div>
			<div className="flex items-center gap-2 px-5 pb-4">
				<Input
					name="snooze-time"
					type="time"
					aria-label="Time"
					value={time}
					onChange={(e) => setTime(e.target.value)}
				/>
				<Button type="submit">Snooze</Button>
			</div>
			<KeyboardHints
				hints={[
					{ keys: ["⏎"], label: "to snooze" },
					{ keys: ["esc"], label: "to go back" },
				]}
			/>
		</form>
	)
}

function KeyboardHints({
	hints,
}: {
	hints: Array<{ keys: string[]; label: string }>
}) {
	return (
		<div className="flex select-none items-center gap-5 border-t border-gray-200 px-5 py-3 text-xs text-secondary dark:border-gray-700/80">
			{hints.map((hint) => (
				<div key={hint.label} className="flex items-center gap-1.5">
					{hint.keys.map((key) => (
						<Shortcut key={key}>{key}</Shortcut>
					))}
					<span className="ml-0.5">{hint.label}</span>
				</div>
			))}
		</div>
	)
}
